import type { ModuleHost, ModuleWebEntry } from '@titan/module-kit';
import { MANIFESTS, manifestFor } from '@titan/modules';
import type { ModuleKey } from '@titan/shared';
import { Suspense, lazy, useMemo, type ComponentType } from 'react';
import { useRoutes, type RouteObject } from 'react-router';
import { useSession } from '../session/SessionProvider.js';
import { Loading, ScreenBody, ScreenHeader } from '../shell/Shell.js';
import {
  Actions,
  Button,
  Card,
  ChipSelector,
  EmptyState,
  Field,
  InlineError,
  InlineInfo,
  KpiCard,
  KpiStrip,
  ListContainer,
  ListHeader,
  ListRow,
  Muted,
  Pill,
  PrimaryCell,
  ProgressBar,
  RowMeta,
  RowText,
  RowTitle,
  SecondaryCell,
  Select,
  TextArea,
  TextInput,
  Toggle,
  TwoColumn,
} from '../ui/index.js';

/**
 * Where each module's screens come from. `pnpm new-module <key>` appends an entry.
 * Lazy imports keep every module in its own chunk.
 */
const WEB_ENTRIES: Partial<Record<ModuleKey, () => Promise<{ createRoutes: ModuleWebEntry }>>> = {
  tick: () => import('@titan/module-tick/web'),
};

/** The shell's primitives, handed to modules through the module-kit contract. */
const host = (): ModuleHost['ui'] => ({
  ScreenHeader,
  ScreenBody,
  Card,
  TwoColumn,
  KpiStrip,
  KpiCard,
  ListContainer,
  ListHeader,
  ListRow,
  PrimaryCell,
  SecondaryCell,
  RowTitle,
  RowMeta,
  RowText,
  EmptyState,
  Pill,
  Button: Button as ModuleHost['ui']['Button'],
  ChipSelector,
  Field,
  TextInput,
  TextArea,
  Select: Select as ModuleHost['ui']['Select'],
  Toggle,
  ProgressBar,
  Actions,
  InlineError,
  InlineInfo,
  Muted,
  Loading: Loading as ComponentType<Record<string, never>>,
});

function ModuleRoutes({ routesFor, manifestKey }: { routesFor: ModuleWebEntry; manifestKey: ModuleKey }) {
  const { me, api } = useSession();
  const manifest = manifestFor(manifestKey);
  const routes = useMemo<RouteObject[]>(() => {
    if (!me || !manifest) return [];
    return routesFor({ manifest, actor: me.actor, api, apiBase: `/m/${manifestKey}`, ui: host() });
  }, [me, manifest, api, routesFor, manifestKey]);
  return useRoutes(routes);
}

/** Mounts a module at its base path, loading its code on first visit. */
export function ModuleMount({ moduleKey }: { moduleKey: ModuleKey }) {
  const load = WEB_ENTRIES[moduleKey];
  const Lazy = useMemo(
    () =>
      load
        ? lazy(async () => {
            const mod = await load();
            return { default: () => <ModuleRoutes routesFor={mod.createRoutes} manifestKey={moduleKey} /> };
          })
        : null,
    [load, moduleKey],
  );
  if (!Lazy) return null;
  return (
    <Suspense fallback={<Loading />}>
      <Lazy />
    </Suspense>
  );
}

/** Route objects for every registered module, mounted at `<basePath>/*`. */
export function moduleRouteObjects(): RouteObject[] {
  return MANIFESTS.filter((m) => WEB_ENTRIES[m.key]).map((m) => ({ path: `${m.basePath}/*`, element: <ModuleMount moduleKey={m.key} /> }));
}
